import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  submitInquiry,
  resetInquiryState,
} from "../store/slices/inquiriesSlice";
import { Section, Container, SectionEyebrow } from "../components/ui/Section";
import {
  StaggerReveal,
  MotionItem,
  fadeUp,
  fadeUpSmall,
  fadeLeft,
} from "../components/ui/Motion";
import {
  PageHeader,
  Title,
  HeaderSub,
  ContentGrid,
  FormCard,
  FormTitle,
  FormRow,
  FormField,
  Label,
  Input,
  SelectWrapper,
  SelectTrigger,
  SelectDropdown,
  SelectOption,
  Textarea,
  SubmitBtn,
  SuccessMsg,
  InfoSide,
  InfoCard,
  InfoIcon,
  InfoText,
  InfoLabel,
  InfoValue,
  InfoLink,
  // HoursList,
  // HoursRow,
  // HoursDay,
  // HoursTime,
  // HoursClosed,
  SocialBlock,
  SocialLabel,
  SocialRow,
  SocialLink,
} from "./styles/ContactPage.styles";

const MailIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </svg>
);

// const PhoneIcon = () => (
//   <svg
//     viewBox="0 0 24 24"
//     fill="none"
//     stroke="currentColor"
//     strokeWidth="1.5"
//     strokeLinecap="round"
//     strokeLinejoin="round"
//   >
//     <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z" />
//   </svg>
// );

// const PinIcon = () => (
//   <svg
//     viewBox="0 0 24 24"
//     fill="none"
//     stroke="currentColor"
//     strokeWidth="1.5"
//     strokeLinecap="round"
//     strokeLinejoin="round"
//   >
//     <path d="M12 22s7-7.58 7-13a7 7 0 1 0-14 0c0 5.42 7 13 7 13z" />
//     <circle cx="12" cy="9" r="2.5" />
//   </svg>
// );

// const ClockIcon = () => (
//   <svg
//     viewBox="0 0 24 24"
//     fill="none"
//     stroke="currentColor"
//     strokeWidth="1.5"
//     strokeLinecap="round"
//     strokeLinejoin="round"
//   >
//     <circle cx="12" cy="12" r="9" />
//     <path d="M12 7v5l3 2" />
//   </svg>
// );

const InstagramIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="0.6" fill="currentColor" />
  </svg>
);

// const FacebookIcon = () => (
//   <svg
//     viewBox="0 0 24 24"
//     fill="none"
//     stroke="currentColor"
//     strokeWidth="1.5"
//     strokeLinecap="round"
//     strokeLinejoin="round"
//   >
//     <path d="M14 9h3V5h-3a4 4 0 0 0-4 4v2H7v4h3v6h4v-6h3l1-4h-4V9.5A.5.5 0 0 1 14.5 9z" />
//   </svg>
// );

// const LinkedInIcon = () => (
//   <svg
//     viewBox="0 0 24 24"
//     fill="none"
//     stroke="currentColor"
//     strokeWidth="1.5"
//     strokeLinecap="round"
//     strokeLinejoin="round"
//   >
//     <rect x="3" y="3" width="18" height="18" rx="2" />
//     <path d="M8 10v7" />
//     <path d="M8 7v.01" />
//     <path d="M12 17v-4a2 2 0 0 1 4 0v4" />
//     <path d="M12 10v7" />
//   </svg>
// );

function ContactPage() {
  const dispatch = useDispatch();
  const { submitting, success, error } = useSelector(
    (state) => state.inquiries,
  );
  const prevSuccess = useRef(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    subject: "General Inquiry",
    message: "",
  });
  const [selectOpen, setSelectOpen] = useState(false);
  const selectRef = useRef(null);

  const subjectOptions = [
    "General Inquiry",
    "New Yacht Purchase",
    "Brokerage Inquiry",
    "Bespoke Build",
    "Partnership",
    "Other",
  ];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (selectRef.current && !selectRef.current.contains(e.target)) {
        setSelectOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => dispatch(resetInquiryState());
  }, [dispatch]);

  useEffect(() => {
    if (success && !prevSuccess.current) {
      toast.success("Message sent! We'll get back to you within 24 hours.");
    }
    prevSuccess.current = success;
  }, [success]);

  useEffect(() => {
    if (error) {
      toast.error("Failed to send message. Please try again.");
    }
  }, [error]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(submitInquiry({ ...formData, source: "contact" }));
  };

  return (
    <>
      <PageHeader>
        <Container>
          <StaggerReveal>
            <MotionItem $variants={fadeUpSmall}>
              <SectionEyebrow>Get in Touch</SectionEyebrow>
            </MotionItem>
            <MotionItem $variants={fadeUp}>
              <Title>Contact Us</Title>
            </MotionItem>
            <MotionItem $variants={fadeUpSmall}>
              <HeaderSub>
                Have a question about a yacht or our services? We'd love to hear
                from you.
              </HeaderSub>
            </MotionItem>
          </StaggerReveal>
        </Container>
      </PageHeader>

      <Section>
        <Container>
          <ContentGrid>
            {/* Form */}
            <StaggerReveal>
              <MotionItem $variants={fadeLeft}>
                <FormCard>
                  {success ? (
                    <SuccessMsg>
                      <h3>Message Sent!</h3>
                      <p>
                        Thank you for reaching out. We'll get back to you within
                        24 hours.
                      </p>
                    </SuccessMsg>
                  ) : (
                    <form onSubmit={handleSubmit}>
                      <FormTitle>Send us a message</FormTitle>
                      <FormRow>
                        <FormField>
                          <Label>Full Name</Label>
                          <Input
                            type="text"
                            name="fullName"
                            placeholder="John Doe"
                            required
                            value={formData.fullName}
                            onChange={handleChange}
                          />
                        </FormField>
                        <FormField>
                          <Label>Email</Label>
                          <Input
                            type="email"
                            name="email"
                            placeholder="john@example.com"
                            required
                            value={formData.email}
                            onChange={handleChange}
                          />
                        </FormField>
                      </FormRow>
                      <FormRow>
                        <FormField>
                          <Label>Phone</Label>
                          <Input
                            type="tel"
                            name="phone"
                            placeholder="+381 ..."
                            value={formData.phone}
                            onChange={handleChange}
                          />
                        </FormField>
                        <FormField>
                          <Label>Subject</Label>
                          <SelectWrapper ref={selectRef}>
                            <SelectTrigger
                              type="button"
                              $open={selectOpen}
                              $hasValue={!!formData.subject}
                              onClick={() => setSelectOpen(!selectOpen)}
                            >
                              {formData.subject}
                            </SelectTrigger>
                            {selectOpen && (
                              <SelectDropdown>
                                {subjectOptions.map((option) => (
                                  <SelectOption
                                    key={option}
                                    $active={formData.subject === option}
                                    onClick={() => {
                                      setFormData({
                                        ...formData,
                                        subject: option,
                                      });
                                      setSelectOpen(false);
                                    }}
                                  >
                                    {option}
                                  </SelectOption>
                                ))}
                              </SelectDropdown>
                            )}
                          </SelectWrapper>
                        </FormField>
                      </FormRow>
                      <FormField>
                        <Label>Message</Label>
                        <Textarea
                          name="message"
                          placeholder="Tell us how we can help..."
                          required
                          value={formData.message}
                          onChange={handleChange}
                        />
                      </FormField>
                      <SubmitBtn type="submit" disabled={submitting}>
                        {submitting ? "Sending..." : "Send Message"}
                      </SubmitBtn>
                    </form>
                  )}
                </FormCard>
              </MotionItem>
            </StaggerReveal>

            {/* Contact info */}
            <InfoSide slow>
              <InfoCard $variants={fadeUpSmall}>
                <InfoIcon>
                  <MailIcon />
                </InfoIcon>
                <InfoText>
                  <InfoLabel>General</InfoLabel>
                  <InfoValue>
                    <InfoLink href="mailto:info@lumayachting.com">
                      info@lumayachting.com
                    </InfoLink>
                  </InfoValue>
                </InfoText>
              </InfoCard>
              {/* 
              <InfoCard $variants={fadeUpSmall}>
                <InfoIcon>
                  <MailIcon />
                </InfoIcon>
                <InfoText>
                  <InfoLabel>Brokers</InfoLabel>
                  <InfoValue>
                    <InfoLink href="mailto:luka@lumayachting.com">
                      luka@lumayachting.com
                    </InfoLink>
                  </InfoValue>
                  <InfoValue>
                    <InfoLink href="mailto:mz@lumayachting.com">
                      mz@lumayachting.com
                    </InfoLink>
                  </InfoValue>
                </InfoText>
              </InfoCard> */}

              <SocialBlock $variants={fadeUpSmall}>
                <SocialLabel>Follow Us</SocialLabel>
                <SocialRow>
                  <SocialLink
                    href="https://www.instagram.com/lumayachting/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                  >
                    <InstagramIcon />
                  </SocialLink>
                </SocialRow>
              </SocialBlock>
            </InfoSide>
          </ContentGrid>
        </Container>
      </Section>
    </>
  );
}

export default ContactPage;
